<script setup>
import { ref, computed, watch } from "vue";
import Layout from "@pages/Shared/layouts/AdminLayout.vue";
import Input from "@pages/Shared/form/Input.vue";
import { PhCheck, PhBook } from "@phosphor-icons/vue";
import { useScoresheet } from "@/Stores/useScoresheet";
import { flashToast } from "@/toast.js";
import { capitalize } from "@/helpers.js";
import logo from "@/../images/logo.jpg";

const props = defineProps({
    classRooms:    { type: Array,  default: () => [] },
    assessments:   { type: Array,  default: () => [] },
    activeSession: { type: Object, default: null },
    activeTerm:    { type: Object, default: null },
});

const scoreSheetStore = useScoresheet();
const selections      = ref({});
const search          = ref("");
const includeAllSubjectsGlobally = ref(false);

const classMap = computed(() => {
    const map = {};
    for (const cls of props.classRooms) map[String(cls.id)] = cls;
    return map;
});

const filteredClasses = computed(() => {
    const query = search.value.trim().toLowerCase();
    if (!query) return props.classRooms;
    return props.classRooms.filter(c => c.name?.toLowerCase().includes(query));
});

const selectedClassIds = computed(() => Object.keys(selections.value));

const totalStudentsSelected = computed(() =>
    selectedClassIds.value.reduce((sum, id) =>
        sum + (classMap.value[id]?.students?.length ?? 0), 0)
);

const groupedBySection = computed(() => {
    const groups = {};
    for (const cls of filteredClasses.value) {
        const section = cls.section?.name || "Uncategorized";
        if (!groups[section]) groups[section] = [];
        groups[section].push(cls);
    }
    return groups;
});

const toggleClass = (classId) => {
    const id = String(classId);
    if (selections.value[id] !== undefined) {
        const updated = { ...selections.value };
        delete updated[id];
        selections.value = updated;
    } else {
        selections.value = { ...selections.value, [id]: [] };
    }
};

const selectAllClasses = () => {
    const newSelection = {};
    for (const cls of props.classRooms) newSelection[String(cls.id)] = [];
    selections.value = newSelection;
};

const clearAll = () => { selections.value = {}; };

const isSubjectSelected = (classId, subjectId) => {
    const selected = selections.value[String(classId)];
    return selected !== undefined && (selected.length === 0 || selected.includes(subjectId));
};

const toggleSubject = (classId, subjectId) => {
    const id       = String(classId);
    const current  = selections.value[id] ?? [];
    const subjects = classMap.value[id]?.subjects ?? [];

    // If currently "all selected" (empty array = all), seed the full list first then remove
    if (current.length === 0 && subjects.length > 0) {
        selections.value = {
            ...selections.value,
            [id]: subjects.map(s => s.id).filter(sid => sid !== subjectId),
        };
        return;
    }

    const exists = current.includes(subjectId);
    const updated = exists
        ? current.filter(sid => sid !== subjectId)
        : [...current, subjectId];

    // If all subjects are now selected, collapse back to [] (meaning "all")
    selections.value = {
        ...selections.value,
        [id]: updated.length === subjects.length ? [] : updated,
    };
};

watch(includeAllSubjectsGlobally, (enabled) => {
    if (enabled) {
        const reset = {};
        for (const id of selectedClassIds.value) reset[id] = [];
        selections.value = reset;
    }
});

const generatePDF = async () => {
    if (!selectedClassIds.value.length)
        return flashToast({ status: false, message: "Please select at least one class." });

    if (!props.activeTerm)
        return flashToast({ status: false, message: "No active term found." });

    const classesData = selectedClassIds.value.map(classId => {
        const cls = classMap.value[String(classId)];
        if (!cls) return null;

        const selectedSubjectIds = selections.value[String(classId)] ?? [];
        const selectedSubjects   = selectedSubjectIds.length > 0
            ? cls.subjects.filter(s => selectedSubjectIds.includes(s.id))
            : cls.subjects ?? [];

        return {
            id: cls.id,
            name: cls.name,
            students: cls.students ?? [],
            selectedSubjects,
        };
    }).filter(Boolean);

    try {
        await scoreSheetStore.generateScoreSheet({
            classesData,
            assessments:  props.assessments,
            termName:     props.activeTerm.name,
            sessionYear:  props.activeSession?.year ?? new Date().getFullYear(),
            logo,
        });
        flashToast({ status: true, message: "Score sheets generated successfully." });
    } catch (error) {
        console.error(error);
        flashToast({ status: false, message: "Generation failed. Please try again." });
    }
};
</script>

<template>
    <Layout>

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Score Sheets</h2>
                <p class="text-sm text-gray-500 mt-0.5">
                    Session: <span class="font-medium">{{ activeSession?.name ?? '—' }}</span>
                    &nbsp;·&nbsp;
                    Term: <span class="font-medium">{{ activeTerm?.name ?? '—' }}</span>
                </p>
            </div>
            <div v-if="!activeTerm"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                       bg-red-50 text-red-600">
                <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                No active term
            </div>
        </div>

        <!-- Control Bar -->
        <div class="bg-white border border-gray-200 shadow rounded-lg overflow-hidden mb-6">
            <div class="flex flex-col md:flex-row md:items-center md:justify-between
                        gap-3 px-4 py-3 border-b border-gray-200">

                <div class="flex flex-wrap items-center gap-2">
                    <button
                        @click="selectedClassIds.length ? clearAll() : selectAllClasses()"
                        class="px-3 py-1.5 text-sm font-medium rounded-lg border border-primary/20
                               text-primary hover:bg-primary/5 transition"
                    >
                        {{ selectedClassIds.length ? 'Clear Selection' : 'Select All' }}
                    </button>

                    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none px-1">
                        <input type="checkbox" v-model="includeAllSubjectsGlobally"
                            class="w-4 h-4 rounded border-gray-300 accent-primary cursor-pointer" />
                        Include all subjects
                    </label>

                    <span v-if="selectedClassIds.length" class="text-sm text-gray-500">
                        <span class="font-semibold text-gray-700">{{ selectedClassIds.length }}</span>
                        {{ selectedClassIds.length === 1 ? 'class' : 'classes' }} ·
                        <span class="font-semibold text-gray-700">{{ totalStudentsSelected }}</span>
                        students
                    </span>
                </div>

                <Input v-model="search" placeholder="Search class..." class="w-full md:w-64" />
            </div>

            <!-- Sectioned Class Cards -->
            <div class="p-4 space-y-8">
                <div v-for="(classes, sectionName) in groupedBySection" :key="sectionName">

                    <div class="flex items-center justify-between mb-3">
                        <h3 class="text-xs font-bold uppercase tracking-widest text-gray-500">
                            {{ capitalize(sectionName) }}
                        </h3>
                        <span class="text-xs text-gray-500">{{ classes.length }} classes</span>
                    </div>

                    <div class="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                        <div
                            v-for="cls in classes" :key="cls.id"
                            class="bg-white border rounded-xl p-5 transition-all duration-200 hover:shadow-md cursor-pointer"
                            :class="selections[String(cls.id)] !== undefined
                                ? 'border-primary ring-2 ring-primary/10'
                                : 'border-gray-200 hover:-translate-y-0.5'"
                            @click="toggleClass(cls.id)"
                        >
                            <!-- Card Header -->
                            <div class="flex justify-between items-start">
                                <div>
                                    <p class="text-sm font-bold text-primary">{{ capitalize(cls.name) }}</p>
                                    <p class="text-xs text-gray-500 mt-0.5">
                                        {{ cls.students?.length ?? 0 }} students ·
                                        {{ cls.subjects?.length ?? 0 }} subjects
                                    </p>
                                </div>
                                <span
                                    class="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all"
                                    :class="selections[String(cls.id)] !== undefined
                                        ? 'bg-primary border-primary'
                                        : 'border-gray-300'"
                                    @click.stop="toggleClass(cls.id)"
                                >
                                    <PhCheck v-if="selections[String(cls.id)] !== undefined"
                                        :size="11" weight="bold" class="text-white" />
                                </span>
                            </div>

                            <!-- Inline Subject Chips -->
                            <div
                                v-if="selections[String(cls.id)] !== undefined && !includeAllSubjectsGlobally && cls.subjects?.length"
                                class="mt-4 pt-4 border-t border-gray-100"
                                @click.stop
                            >
                                <p class="text-xs text-gray-400 mb-2">
                                    Select subjects
                                    <span class="text-gray-300 ml-1">
                                        ({{ selections[String(cls.id)].length === 0 ? 'all' : selections[String(cls.id)].length }} selected)
                                    </span>
                                </p>
                                <div class="flex flex-wrap gap-1.5">
                                    <button
                                        v-for="subject in cls.subjects" :key="subject.id"
                                        type="button"
                                        @click.stop="toggleSubject(cls.id, subject.id)"
                                        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border transition-all"
                                        :class="isSubjectSelected(cls.id, subject.id)
                                            ? 'bg-primary-50 text-primary border-primary-100'
                                            : 'bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-300'"
                                    >
                                        <PhCheck
                                            v-if="isSubjectSelected(cls.id, subject.id)"
                                            :size="10" class="text-edit" />
                                        <PhBook
                                            v-else
                                            :size="10" class="text-gray-400" />
                                        {{ subject.name }}
                                    </button>
                                </div>
                            </div>

                            <!-- All subjects included notice -->
                            <div
                                v-else-if="selections[String(cls.id)] !== undefined && includeAllSubjectsGlobally && cls.subjects?.length"
                                class="mt-4 pt-4 border-t border-gray-100"
                            >
                                <div class="flex flex-wrap gap-1.5">
                                    <span
                                        v-for="subject in cls.subjects" :key="subject.id"
                                        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs
                                               bg-primary-50 text-primary rounded-md border border-primary-100"
                                    >
                                        <PhCheck :size="10" class="text-edit" />
                                        {{ subject.name }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="!Object.keys(groupedBySection).length"
                    class="py-12 text-center text-sm text-gray-400">
                    No classes found.
                </div>
            </div>
        </div>

        <!-- Floating Generate Button -->
        <div class="fixed bottom-6 right-6 z-50">
            <div class="bg-white border border-gray-200 shadow-xl rounded-xl px-5 py-3.5
                        flex items-center gap-5">
                <div class="text-sm text-gray-500">
                    <span class="font-bold text-gray-800">{{ selectedClassIds.length }}</span> classes ·
                    <span class="font-bold text-gray-800">{{ totalStudentsSelected }}</span> students
                </div>
                <button
                    :disabled="scoreSheetStore.isGenerating || !selectedClassIds.length || !activeTerm"
                    @click="generatePDF"
                    class="px-5 py-2.5 rounded-lg bg-primary hover:bg-primary-light text-white text-sm
                           font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {{ scoreSheetStore.isGenerating ? 'Generating…' : 'Generate Sheets' }}
                </button>
            </div>
        </div>

    </Layout>
</template>